import { useEffect, useRef, useState } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRevealWage } from "../../hooks/useRevealWage";
import type { WageBreakdownReopen } from "../../utils/navigation/wageBreakdownReturn";
import { useWageBreakdownReopen } from "./WageBreakdownReopenContext";

/** A row a breakdown can open under, and the collapsible group (location group, Actual section) holding it. */
export type ReopenCandidate = { shiftId: string; groupKey: string };

/**
 * DEFAULT: how long the table's rows must go unchanged, without the shift among them, before the reopen is
 * given up. The day has no single "loaded" signal — the Actual view assembles its sections from the staff, the
 * invalid rows and the punch summary, each arriving on its own — so a row set that is still changing is
 * still loading. Long enough to outlast those arrivals; short enough that a later click on another row is
 * never hijacked by a stale request.
 */
export const REOPEN_SETTLE_MS = 3000;

/** Why a reopen was given up, as `wage_breakdown_reopen_missed` reports it. */
type MissReason = "wages_hidden" | "no_rows" | "row_not_found";

type Params = {
	/** Every row on screen that can open a breakdown, with its group. Memoise it: it is an effect dependency. */
	candidates: readonly ReopenCandidate[];
	/** Which view these rows are: a reopen for the other view is left for it. */
	isActual: boolean;
	/** Opens a group (both views' groups start collapsed). Stable. */
	openGroup: (groupKey: string) => void;
	/** Opens the breakdown under a row. Stable. */
	openRow: (shiftId: string) => void;
	/** The row whose breakdown is open now: once it is another (or none), the reopen has been consumed. */
	expandedRow: string | null;
};

/**
 * DAT-906 · Applies a pending "reopen this shift's breakdown" once its row is in the table: opens the row's
 * group and the breakdown under it, and returns the request so its panel can scroll itself into view and
 * unfold its rule lines if they were unfolded when the user left —
 * until that breakdown is closed, so opening it again later does not scroll again.
 *
 * With wages hidden (no breakdown can open), or once the rows have settled without the shift (it moved to
 * another day, or a filter hides it), the request is dropped — and reported, never silently.
 */
export const useReopenWageBreakdownRow = ({ candidates, isActual, openGroup, openRow, expandedRow }: Params): WageBreakdownReopen | null => {
	const { pending, markApplied } = useWageBreakdownReopen();
	const canViewWage = useRevealWage();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const [reopened, setReopened] = useState<WageBreakdownReopen | null>(null);

	// The latest `track`, so a new analytics context doesn't restart the settle wait below.
	const trackRef = useRef(track);
	useEffect(() => {
		trackRef.current = track;
	}, [track]);

	// Adjusted during render (React's "storing information from previous renders"): the breakdown it scrolled
	// to was closed, or its row left the table (a filter hid it), so the next time that row's breakdown mounts
	// it is an ordinary open — no second scroll, and the viewer's fold choice is not overridden.
	if (reopened !== null && (expandedRow !== reopened.shiftId || !candidates.some((candidate) => candidate.shiftId === reopened.shiftId))) {
		setReopened(null);
	}

	useEffect(() => {
		if (pending === null || pending.isActual !== isActual) return;
		const miss = (reason: MissReason) => {
			trackRef.current("wage_breakdown_reopen_missed", {
				department_id: chosenDepartment.department_id,
				shift_id: pending.shiftId,
				is_actual: pending.isActual,
				reason,
				row_count: candidates.length,
			});
			markApplied();
		};
		if (!canViewWage) {
			miss("wages_hidden");
			return;
		}
		const target = candidates.find((candidate) => candidate.shiftId === pending.shiftId);
		if (target === undefined) {
			// Not here yet. A change to the rows re-runs this effect and restarts the wait.
			const timer = setTimeout(() => miss(candidates.length === 0 ? "no_rows" : "row_not_found"), REOPEN_SETTLE_MS);
			return () => clearTimeout(timer);
		}
		openGroup(target.groupKey);
		openRow(target.shiftId);
		setReopened(pending);
		markApplied();
	}, [pending, isActual, canViewWage, candidates, openGroup, openRow, markApplied, chosenDepartment.department_id]);

	return reopened;
};
